import React, { useEffect, useState } from 'react';

import { isEmpty } from 'lodash';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { isStringHasRealContent } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useHashValue } from 'hooks/useHashValue';

import { useCommentsStore } from 'store/comments.store';
import { useCommentsEditorStatus } from 'store/commentsEditorStatus.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationCreateCard } from 'api/mutations/createCard.mutation';
import { useMutationCreateEditComment } from 'api/mutations/createEditComment.mutation';
import { useQueryGetBoardInitLite } from 'api/queries/getBoardInitLite.query';

import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ConfirmChangesModal } from 'components/common/confirmChangesModal/confirmChangesModal';

import {
    IActiveTab,
    TabsSection,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/TabsSection/TabsSection';
import { useMainCardProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/MainCard.provider';
import { mapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/MainCard.utils';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/RecommendationCardModal/RecommendationCardModal.module.scss';
import {
    buildRecommendationCardPayload,
    tabsArrData,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/RecommendationCardModal/RecommendationCardModal.utils';
import { IFields } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/RecommendationCardModal/RecommendationCardModal.utils.types';
import { CardContent } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/RecommendationCardModal/components/CardContent/CardContent';
import { CardHeader } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/RecommendationCardModal/components/CardHeader/CardHeader';
import { RecommendationCardStatusSelect } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/RecommendationCardModal/components/RecommendationCardStatusSelect/RecommendationCardStatusSelect';
import { SideMenu } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/RecommendationCardModal/components/SideMenu/SideMenu';
import {
    assigneeMapper,
    versionMapper,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';

interface IRecommendationCardProps {
    saveCardCallback: (value?: number) => void;
    deleteCardCallback: () => void;
    queryKeys?: string[];
}

export const RecommendationCardModal = ({
    saveCardCallback,
    deleteCardCallback,
    queryKeys = [],
}: IRecommendationCardProps) => {
    const { showModal, setShowModal, isMobile, api } = useMainCardProvider();
    const commentData = useCommentsStore((store) => store.commentData);
    const { resetCommentData, setCommentData, setCommentId } = useCommentsStore();
    const { resetCommentsEditorStatus } = useCommentsEditorStatus();

    const queryClient = useQueryClient();

    const hashValue = useHashValue();
    const activeTabByHash = hashValue ? (hashValue as IActiveTab) : 'cardDetails';
    const { pathname, search } = useLocation();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<IActiveTab>(activeTabByHash);

    useEffect(() => {
        hashValue && setActiveTab(hashValue as IActiveTab);
    }, [hashValue]);

    const history = useHistory();

    const { project } = useProjectTreeStore();
    const { data: boardInit, isLoading: boardInitLoading } = useQueryGetBoardInitLite({ projectId: project?.id });
    const { mutate: mutateCreateCard, isLoading: createCardLoading } = useMutationCreateCard();
    const { mutate: createEditCommentMutate, isLoading: createEditCommentLoading } = useMutationCreateEditComment();

    const { handleSnackBar } = useCustomSnackBar();

    const isLoading = boardInitLoading || createCardLoading || createEditCommentLoading;

    const {
        control,
        handleSubmit,
        formState: { errors, dirtyFields },
        setValue,
        getValues,
        watch,
        reset,
    } = useForm<IFields>();

    // reset comments on first load - because 'handleModalClose' execute only on Cancel button click
    useEffect(() => {
        resetCommentsEditorStatus();
        resetCommentData();
    }, []);

    // ===============================================================================================================

    useEffect(() => {
        const data = api.getCard.data;
        if (boardInit && data) {
            // project
            const currentProject = boardInit.projectContractVersions.find((item) => item.id === data.project.id);

            // contract
            const contractOptions = currentProject?.contracts;
            const currentContract = contractOptions?.find((item) => item.id === data.contract.id);

            // versionName
            const versionOptions = currentContract?.metaDataResponse;
            const currentVersion = versionOptions?.find((item) => item.id === data.metaData.id);

            reset({
                id: data.id,
                project: currentProject ? mapper(currentProject) : null,
                contract: currentContract ? mapper(currentContract) : null,
                versionName: currentVersion ? versionMapper(currentVersion) : null,
                activityId: data.relatedActivity.id,
                title: data.title,
                status: data.status,
                details: data.details,
                mentionedList: data.mentionedList,
                assignee: assigneeMapper(data.assignee),
                category: mapper(data.category),
                isImportant: data.isImportant,
                startDate: data.startDate,
                endDate: data.endDate,
                externalLinks: data.externalLinks,
                versionId: currentVersion?.id,

                relatedCardIds: null,
                type: 'KC_INSIGHT',
            });
        }
    }, [boardInit, api.getCard.data]);

    const handleCommentData = () => {
        if (!isStringHasRealContent(commentData.comment)) {
            createEditCommentMutate(commentData, {
                onSuccess: () => {
                    return Promise.all([queryClient.invalidateQueries('getCard')]).then(() => {
                        resetCommentsEditorStatus();
                        setCommentData({
                            ...commentData,
                            cardId: api.getCard.data?.id,
                            isVirtual: api.getCard.data?.isVirtual,
                            comment: '',
                        });
                        setCommentId(false);
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            });
        }
    };

    const handleSave = (data) => {
        const payload = buildRecommendationCardPayload(data);

        mutateCreateCard(payload, {
            onSuccess: (res) => {
                handleSnackBar('Card Successfully Updated', 'success');
                api.getCard.refetch();
                saveCardCallback(res.id);
                reset(getValues());

                handleCommentData();
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
            onSettled: () => {
                if (showConfirmModal) {
                    handleModalClose();
                }
            },
        });
    };

    const isStateEmpty = isEmpty(getValues());
    const isCardDirty = !isEmpty(dirtyFields) || !isStringHasRealContent(commentData.comment);

    const handleCardClose = () => {
        if (isCardDirty) {
            setShowConfirmModal(true);
        } else {
            handleModalClose();
        }
    };

    const handleConfirmModalSave = () => {
        handleSubmit(handleSave)();
    };

    //==================================== handleModalClose ============================================================
    const handleModalClose = () => {
        resetCommentsEditorStatus();
        resetCommentData();

        const pathWithoutParams = pathname.slice(0, pathname.lastIndexOf('/'));
        setShowConfirmModal(false);
        setShowModal(false);
        history.push({
            pathname: pathWithoutParams,
            search: search,
        });
    };
    //==================================================================================================================

    return (
        <Modal
            open={showModal}
            onClose={handleCardClose}
            showCloseButton={!isMobile}
            width={'140rem'}
            title={'Recommendation'}
            actions={
                <>
                    <Button
                        size={'large'}
                        variant={'secondary'}
                        onClick={handleCardClose}
                        id={'cardModalCloseOrCancelButton'}
                    >
                        Close
                    </Button>
                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        id={'cardModalSaveButton'}
                        disabled={!isCardDirty}
                        form={'recommendation-form'}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <form
                id={'recommendation-form'}
                onSubmit={handleSubmit(handleSave)}
                className={classes.recommendationCardContainer}
            >
                {isLoading && <OverlayWithSpinner />}

                {!isStateEmpty && (
                    <>
                        <SideMenu
                            title={api.getCard.data?.title}
                            id={api.getCard.data?.id}
                            handleModalClose={handleModalClose}
                            deleteCardCallback={deleteCardCallback}
                        />

                        <CardHeader control={control} />
                        <div className={classes.tabsWithStatusWrapper}>
                            <TabsSection tabs={tabsArrData} activeTab={activeTab} setActiveTab={setActiveTab} />
                            <RecommendationCardStatusSelect control={control} />
                        </div>

                        <CardContent
                            activeTab={activeTab}
                            boardInit={boardInit}
                            setValue={setValue}
                            watch={watch}
                            control={control}
                            errors={errors}
                            data={api.getCard.data}
                            queryKeys={queryKeys}
                        />
                    </>
                )}
            </form>

            <ConfirmChangesModal
                showModal={showConfirmModal}
                onHideModal={() => setShowConfirmModal(false)}
                handleCloseWithoutSave={handleModalClose}
                handleSave={handleConfirmModalSave}
                isLoading={createCardLoading}
            >
                You made changes to this card. Would you like to save it?
            </ConfirmChangesModal>
        </Modal>
    );
};
